/**
 * The dashboard's series, derived from the two documents that feed the charts.
 *
 * No imports, deliberately, on the precedent `analytics-window.ts` and
 * `model-usage.ts` set: Node 24 strips types, so a module with only type
 * imports and no JSX runs directly under `node` and the self-check at the foot
 * of this file IS the test suite. Everything the charts on /analytics compute
 * lives here, and `analytics.tsx` computes nothing — the same split
 * `charts-scale.ts` and `charts.tsx` hold to one package over.
 *
 * Two rules run through all of it, and neither is cosmetic:
 *
 *   * **A gap is not a zero.** The overview's series carries an explicit row
 *     for every quiet day at or after `available_from`, so a zero here is a
 *     measurement. An ABSENT money key is not — it is a permission boundary —
 *     and it becomes `null`, which the chart primitives draw as a hole and
 *     count out loud rather than as a day nobody spent anything.
 *   * **Nothing here reads a currency's scale.** Money stays in MICROS all the
 *     way to the renderer, and the screen hands in a formatter built from the
 *     currency the response named. Converting to a float here would put a
 *     rounding error in the hidden table, which is the exact record.
 */

import type { SeriesPoint } from "@empite/ui-components";
import type {
  AnalyticsFleet,
  AnalyticsSeriesPoint,
  AnalyticsWindow,
  ModelUsage,
} from "../admin-client";

/** A named series ready for StackedBarChart / GroupedBarChart. Structural, so
 *  this module needs no value import from `@empite/ui-components`. */
export interface NamedSeries {
  name: string;
  points: SeriesPoint[];
  color?: string;
}

/** One chart's input: the bars' labels, and what goes in them. */
export interface SeriesBundle {
  labels: string[];
  series: NamedSeries[];
}

/**
 * The day labels, full ISO and not shortened.
 *
 * A `MM-DD` axis is narrower and is wrong for exactly one reader in a hundred
 * — the one looking at a window that crosses a new year — and the axis only
 * writes seven of them anyway. The same strings caption the hidden table and
 * open the aria summary, where "2026-01-02 to 2026-01-31" is unambiguous and
 * "01-02 to 01-31" is a date format argument.
 */
export function dayLabels(series: readonly { day: string }[]): string[] {
  return series.map((point) => point.day);
}

/**
 * The three fields an outcome split reads, named STRUCTURALLY rather than as
 * `AnalyticsSeriesPoint`.
 *
 * The overview's series and one application's series are two different wire
 * types carrying the same three fields, and the split of a bar into what
 * succeeded and what did not is the same picture over both. Widening the
 * parameter is what lets the application card draw it without a second copy of
 * the clamp below — and a copy is exactly where the two would drift, since the
 * thing being protected is an invariant of the DATA rather than a choice about
 * the drawing.
 */
export interface OutcomeDay {
  day: string;
  requests: number;
  failed_requests: number;
}

/**
 * Requests split by outcome.
 *
 * Succeeded is DERIVED — `requests` minus `failed_requests` — because the wire
 * carries the total and the failures, and a stack has to be made of parts that
 * add up to the bar. It is clamped at zero: the server's own CHECK constraint
 * says failures cannot exceed requests, so a negative here would be a
 * contradiction in the data rather than a reading, and a negative part is not
 * something a stack can draw at all.
 *
 * Succeeded sits at the BOTTOM. Both orders lose something — failures on the
 * baseline would be the comparable end — and this one is chosen because the
 * failure figure a reader wants is the RATE, which is a tile above the chart
 * and a column in the table under it, while the shape a reader wants from the
 * picture is traffic. A red foundation under every bar of a healthy month
 * says the opposite of what the month did.
 */
export function outcomeSeries(
  series: readonly OutcomeDay[],
  positive: string,
  negative: string,
): SeriesBundle {
  return {
    labels: dayLabels(series),
    series: [
      {
        name: "Succeeded",
        color: positive,
        points: series.map((point) => ({
          label: point.day,
          value: Math.max(0, point.requests - point.failed_requests),
        })),
      },
      {
        name: "Failed",
        color: negative,
        points: series.map((point) => ({ label: point.day, value: point.failed_requests })),
      },
    ],
  };
}

/**
 * Tokens in and tokens out, per day.
 *
 * TWO parts and not the wire's five, and the reason is that a stack of five is
 * a picture of two. Cached input, cache writes and reasoning run at a fraction
 * of a per cent of the other two on real traffic, so all three come out below
 * the resolution of the drawing and the chart renders as input and output with
 * a note apologising for the rest — which is the same picture, plus an
 * apology. The five-way split is in the "Total tokens" tile's hint above, and
 * in this chart's hidden table by way of the two sums.
 *
 * The two still add up to `total_tokens`. That is what makes the roll-up safe:
 * every one of the five kinds is in exactly one of them, so the bar's height
 * agrees with the tile above it rather than topping out below it for a reason
 * nothing on the page states.
 */
export function tokenSeries(series: readonly AnalyticsSeriesPoint[]): SeriesBundle {
  const directions: Array<[string, (point: AnalyticsSeriesPoint) => number]> = [
    // Everything sent upstream: the fresh prompt, the part of it served from
    // the provider's cache, and the writes that put it there.
    ["In", (point) => point.input_tokens + point.cached_input_tokens + point.cache_write_tokens],
    // Everything generated: the completion and the thinking behind it.
    ["Out", (point) => point.output_tokens + point.reasoning_tokens],
  ];
  return {
    labels: dayLabels(series),
    series: directions.map(([name, read]) => ({
      name,
      points: series.map((point) => ({ label: point.day, value: read(point) })),
    })),
  };
}

/**
 * Revenue, cost and margin per day, in MICROS.
 *
 * Grouped rather than stacked, and the reason is in the data: margin is
 * revenue minus cost rather than a part of either, and it goes NEGATIVE on a
 * written-off day — which is the day this chart exists to make visible. A
 * stack can draw neither.
 *
 * An absent key becomes `null` and not zero. Without `wallets:read` the whole
 * chart is absent from the page, so a null here is the narrower case: a day
 * the rollup carries no money figure for, which is not a day that earned
 * nothing.
 */
export function moneySeries(series: readonly AnalyticsSeriesPoint[]): SeriesBundle {
  const measures: Array<[string, (point: AnalyticsSeriesPoint) => number | undefined]> = [
    ["Revenue", (point) => point.revenue_micros],
    ["Provider cost", (point) => point.cost_micros],
    ["Margin", (point) => point.margin_micros],
  ];
  return {
    labels: dayLabels(series),
    series: measures.map(([name, read]) => ({
      name,
      points: series.map((point) => {
        const value = read(point);
        return { label: point.day, value: value === undefined ? null : value };
      }),
    })),
  };
}

/** Active accounts per day. An exact per-day distinct count, so it is a real
 *  reading rather than an upper bound — which is why this one gets a line and
 *  the device count does not. */
export function activeUserSeries(series: readonly AnalyticsSeriesPoint[]): SeriesPoint[] {
  return series.map((point) => ({ label: point.day, value: point.distinct_users }));
}

/**
 * The live device fleet, one part per platform.
 *
 * A SNAPSHOT, not a window: `fleet.by_platform` counts what is live now, and
 * the card that draws it says so. The order is the server's, which is by name,
 * so the colours do not reshuffle between two loads of the same page.
 */
export function platformParts(fleet: AnalyticsFleet): SeriesPoint[] {
  return fleet.by_platform.map((entry) => ({ label: entry.platform, value: entry.active }));
}

/**
 * Requests per requested ALIAS, biggest first.
 *
 * The usage report's key is (alias, provider, provider_model), because one
 * alias can be served by several provider models through fallback. A chart of
 * "what is being asked for" wants the alias, so the provider rows are summed
 * back together here.
 *
 * Ties break on the alias, ascending. Nothing in the wire order is guaranteed
 * and two models on the same figure must not swap places between two loads of
 * the same page.
 */
export function topModelRequests(rows: readonly ModelUsage[], limit: number): SeriesPoint[] {
  const byAlias = new Map<string, number>();
  for (const row of rows) {
    byAlias.set(row.alias, (byAlias.get(row.alias) ?? 0) + row.requests);
  }
  const points = [...byAlias.entries()].map(([label, value]) => ({ label, value }));
  points.sort((a, b) => (b.value - a.value) || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
  return limit > 0 ? points.slice(0, limit) : points;
}

/**
 * Whether two documents describe the same span of days.
 *
 * The usage report and the overview are answered by two routes with two
 * clamps: analytics stops at 90 days, the usage report at 365, and the usage
 * report always ends YESTERDAY while the analytics window can be asked to end
 * in the past. Ask for a fortnight last March and the two cards sit side by
 * side describing different months, so the model card says so rather than
 * borrowing the heading above it.
 */
export function sameWindow(
  window: Pick<AnalyticsWindow, "since" | "until">,
  other: { since: string; until: string } | undefined,
): boolean {
  return other !== undefined && other.since === window.since && other.until === window.until;
}

/* ------------------------------------------------------------------------ *
 * Self-check: `node apps/admin-portal/src/screens/analytics-charts.ts`
 * ------------------------------------------------------------------------ */

/** A series point with only the fields a given check reads, cast at the one
 *  place the cast is honest: the fixtures below never reach a renderer. */
function day(fields: Partial<AnalyticsSeriesPoint> & { day: string }): AnalyticsSeriesPoint {
  return {
    requests: 0,
    failed_requests: 0,
    failure_rate: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total_tokens: 0,
    distinct_users: 0,
    distinct_devices_upper_bound: 0,
    ...fields,
  };
}

function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    // A replacer, because JSON.stringify DROPS an undefined-valued key and
    // this module's whole subject is the difference between an absent figure,
    // a null one and a zero.
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ?? "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `analytics-charts self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  const days = [
    day({ day: "2026-08-01", requests: 10, failed_requests: 2, input_tokens: 100, output_tokens: 40, distinct_users: 3, revenue_micros: 900, cost_micros: 400, margin_micros: 500 }),
    day({ day: "2026-08-02", requests: 0, failed_requests: 0, distinct_users: 0, revenue_micros: 0, cost_micros: 200, margin_micros: -200 }),
  ];

  check("dayLabels", dayLabels(days), ["2026-08-01", "2026-08-02"]);
  check("dayLabels of nothing", dayLabels([]), []);

  // Succeeded is derived, and the quiet day stays a real zero rather than
  // disappearing: the API sent a row for it, so it was measured.
  check("outcomeSeries derives successes", outcomeSeries(days, "P", "N"), {
    labels: ["2026-08-01", "2026-08-02"],
    series: [
      {
        name: "Succeeded",
        color: "P",
        points: [
          { label: "2026-08-01", value: 8 },
          { label: "2026-08-02", value: 0 },
        ],
      },
      {
        name: "Failed",
        color: "N",
        points: [
          { label: "2026-08-01", value: 2 },
          { label: "2026-08-02", value: 0 },
        ],
      },
    ],
  });
  // More failures than requests cannot happen — a CHECK constraint refuses it
  // — but a negative part cannot be drawn at all, so the clamp is here rather
  // than left to produce a bar with a hole in it and no explanation.
  check(
    "outcomeSeries clamps a contradiction at zero",
    outcomeSeries([day({ day: "d", requests: 1, failed_requests: 4 })], "P", "N").series[0]?.points,
    [{ label: "d", value: 0 }],
  );
  check("outcomeSeries of an empty window", outcomeSeries([], "P", "N"), { labels: [], series: [
    { name: "Succeeded", color: "P", points: [] },
    { name: "Failed", color: "N", points: [] },
  ] });

  // Two directions, and every one of the five wire fields lands in exactly one
  // of them — which is the property that keeps the bar's height equal to the
  // "Total tokens" tile a card above it. Distinct powers of ten so a field
  // read into the wrong direction, or read twice, changes the answer.
  const tokens = tokenSeries([
    day({ day: "d", input_tokens: 1, cached_input_tokens: 10, cache_write_tokens: 100, output_tokens: 1000, reasoning_tokens: 10_000, total_tokens: 11_111 }),
  ]);
  check("tokenSeries names two directions", tokens.series.map((entry) => entry.name), ["In", "Out"]);
  check(
    "tokenSeries puts the three sent kinds in and the two generated kinds out",
    tokens.series.map((entry) => entry.points[0]?.value),
    [111, 11_000],
  );
  check(
    "the two directions sum to total_tokens",
    tokens.series.reduce((sum, entry) => sum + ((entry.points[0]?.value as number) ?? 0), 0),
    11_111,
  );
  check("tokenSeries wears no explicit colour", tokens.series.map((entry) => entry.color), [
    undefined,
    undefined,
  ]);
  check("tokenSeries labels every bar with its day", tokens.labels, ["d"]);

  // Money stays in micros, a negative margin survives, and an ABSENT key is a
  // null rather than a zero.
  check("moneySeries keeps micros and a negative margin", moneySeries(days), {
    labels: ["2026-08-01", "2026-08-02"],
    series: [
      { name: "Revenue", points: [{ label: "2026-08-01", value: 900 }, { label: "2026-08-02", value: 0 }] },
      { name: "Provider cost", points: [{ label: "2026-08-01", value: 400 }, { label: "2026-08-02", value: 200 }] },
      { name: "Margin", points: [{ label: "2026-08-01", value: 500 }, { label: "2026-08-02", value: -200 }] },
    ],
  });
  check(
    "moneySeries reads a withheld figure as not measured, never as zero",
    moneySeries([day({ day: "d" })]).series.map((entry) => entry.points[0]?.value),
    [null, null, null],
  );
  check(
    "moneySeries keeps a real zero a zero",
    moneySeries([day({ day: "d", revenue_micros: 0, cost_micros: 0, margin_micros: 0 })]).series.map(
      (entry) => entry.points[0]?.value,
    ),
    [0, 0, 0],
  );

  check("activeUserSeries", activeUserSeries(days), [
    { label: "2026-08-01", value: 3 },
    { label: "2026-08-02", value: 0 },
  ]);

  check(
    "platformParts keeps the server's order",
    platformParts({ by_platform: [{ platform: "android", active: 4 }, { platform: "ios", active: 9 }], active_total: 13, registered: 1, revoked: 0 }),
    [{ label: "android", value: 4 }, { label: "ios", value: 9 }],
  );
  check(
    "platformParts of an empty fleet",
    platformParts({ by_platform: [], active_total: 0, registered: 0, revoked: 0 }),
    [],
  );

  // One alias served by two providers is ONE bar.
  const usage: ModelUsage[] = [
    { alias: "fast", provider: "openai", provider_model: "gpt", requests: 5, distinct_users_daily_sum: 0, input_tokens: 0, output_tokens: 0, cost_micros: 0, days_with_traffic: 1, first_day: "d", last_day: "d" },
    { alias: "fast", provider: "anthropic", provider_model: "haiku", requests: 4, distinct_users_daily_sum: 0, input_tokens: 0, output_tokens: 0, cost_micros: 0, days_with_traffic: 1, first_day: "d", last_day: "d" },
    { alias: "smart", provider: "anthropic", provider_model: "opus", requests: 9, distinct_users_daily_sum: 0, input_tokens: 0, output_tokens: 0, cost_micros: 0, days_with_traffic: 1, first_day: "d", last_day: "d" },
    { alias: "cheap", provider: "openai", provider_model: "mini", requests: 9, distinct_users_daily_sum: 0, input_tokens: 0, output_tokens: 0, cost_micros: 0, days_with_traffic: 1, first_day: "d", last_day: "d" },
  ];
  check("topModelRequests sums an alias over its providers", topModelRequests(usage, 10), [
    // fast is 9 too, and the three-way tie breaks on the alias so the order is
    // the same on every load.
    { label: "cheap", value: 9 },
    { label: "fast", value: 9 },
    { label: "smart", value: 9 },
  ]);
  check("topModelRequests takes the top N", topModelRequests(usage, 2), [
    { label: "cheap", value: 9 },
    { label: "fast", value: 9 },
  ]);
  check(
    "topModelRequests ranks by figure before alias",
    topModelRequests(
      [
        { ...(usage[0] as ModelUsage), alias: "zebra", requests: 100 },
        { ...(usage[0] as ModelUsage), alias: "alpha", requests: 1 },
      ],
      10,
    ),
    [{ label: "zebra", value: 100 }, { label: "alpha", value: 1 }],
  );
  check("topModelRequests of nothing", topModelRequests([], 5), []);
  check("topModelRequests with no limit returns everything", topModelRequests(usage, 0).length, 3);

  const window = { since: "2026-08-01", until: "2026-08-30" };
  check("sameWindow agrees", sameWindow(window, { since: "2026-08-01", until: "2026-08-30" }), true);
  check("sameWindow on a different start", sameWindow(window, { since: "2026-07-01", until: "2026-08-30" }), false);
  check("sameWindow on a different end", sameWindow(window, { since: "2026-08-01", until: "2026-08-29" }), false);
  check("sameWindow with nothing to compare", sameWindow(window, undefined), false);

  console.log(`analytics-charts self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
